import { defineEffect } from '../../../core/effect';
import { seg, tween } from '../../../core/effect/motion';
import { add, canvasOf, playerColor } from '../fx/common';
import { SNAP, sightMotion } from '../fx/targetSight';
import {
  bigBox, labelOf, layout, layoutBounds, paintElsewhere, pileSpot, restBox, showsFace,
} from '../fx/targetElsewhere';

/**
 * **対象に取る（盤に無いカード）**（Issue #597。指示書 `../direction/become_target_elsewhere.md`）。
 *
 * 墓地・除外・デッキ・EX の山（手札なら HUD の手札）から対象のカードを引き出し、場と同じ照準の括弧を締めて、
 * **山がある側の画面の端に残す**（v3。印 `targetSight`。場の照準と同じ印・同じ消え方）。カードの下には置き場のラベルの札。
 *
 * ```
 * 0     山の上にカードが現れ、山の上へ・盤の外寄りへ引き出される（高さ 500＝画面の半分弱。後ろに硬い線）。ラベルの札は最初から付いている
 * 250   照準の括弧が外から締まる（場の照準と同じ動き `sightMotion`。最後の 2 フレームだけ白く）
 * 450   2 回脈打つ（番号の札が出る）
 * 600   縮みながら、その山がある側の画面の端へ動く（高さ 300。山とのつながりが動きで分かる。v3）
 * 700   止まる。ここから盤に置く印（`drawObjects.js::targetSight` の elsewhere）が同じ絵を引き継ぎ、控えめに脈打ち続ける
 * ```
 *
 * - 置く場所・塗り方は `../fx/targetElsewhere.js`（帯と印が同じ関数を呼ぶ）。括弧・入れ子・番号の札は `../fx/targetSight.js`
 * - 印の key は場と同じ `<チェーンの番号>:<uid>` なので、そのリンクの `chain_solved` / 無効のリンクの `chain_solving` / `chain_end` で消える
 * - 表で見せるのは墓地・表の除外だけ。手札・デッキ・EX は裏（スリーブ）
 * - カードがその置き場を離れたら印は描かない（`drawObjects.js`）。盤のカードは触らない（motion なし）
 */
export const D = 700;
export const PULL = 250;
export const PULSE = PULL + SNAP;     // 450
export const SHRINK = 600;
const FADE_IN = 40;

/** 対象ごとの組み立て（置き場・同じ置き場の何枚目か・表か・カードの code） */
export function targetsOf(payload, board = null) {
  const list = (payload?.targets ?? []).filter((t) => t?.uid && t?.zone);
  return list.map((t) => {
    const slot = list.filter((u, j) => u.zone === t.zone && j < list.indexOf(t)).length;
    const count = list.filter((u) => u.zone === t.zone).length;
    const onBoard = board?.byUid?.(t.uid)?.card ?? null;
    const fromPayload = (payload?.cards ?? []).find((c) => c?.uid === t.uid) ?? null;
    const code = onBoard?.code ?? fromPayload?.code ?? null;
    return {
      uid: t.uid, zone: t.zone, slot, count, code: code || null, faceUp: showsFace(t.zone, onBoard),
    };
  });
}

const lerp = (a, b, k) => a + (b - a) * k;
const lerpBox = (a, b, k) => ({ cx: lerp(a.cx, b.cx, k), cy: lerp(a.cy, b.cy, k), w: lerp(a.w, b.w, k), h: lerp(a.h, b.h, k) });

/**
 * 帯の時刻 → カードの箱・括弧の見え方。
 * @returns `{ box, k, sightAlpha, flash, line, badge, cardAlpha, streak }`（見えなければ null）
 */
export function frameAt(t, { start, big, rest }) {
  if (!(t >= 0) || t >= D) return null;
  if (t < PULL) {
    const k = tween(t, 0, PULL, 'power3_out');
    return {
      box: lerpBox(start, big, k), k: 1, sightAlpha: 0, flash: 0, line: 1, badge: false,
      cardAlpha: seg(t, 0, FADE_IN), streak: 1 - seg(t, PULL * 0.6, PULL + 60),
    };
  }
  const streak = 1 - seg(t, PULL * 0.6, PULL + 60);
  if (t < PULSE) {
    const m = sightMotion(t - PULL);
    return { box: big, k: m.k, sightAlpha: m.alpha, flash: m.flash, line: m.line, badge: m.k < 1.05, cardAlpha: 1, streak };
  }
  if (t < SHRINK) {
    // 場の照準の 2 回の脈（SNAP → SETTLE の 300ms）を 150ms に詰める
    const m = sightMotion(SNAP + (t - PULSE) * 2);
    return { box: big, k: m.k, sightAlpha: 1, flash: m.flash, line: m.line, badge: true, cardAlpha: 1, streak: 0 };
  }
  const u = tween(t, SHRINK, D, 'power3_out');
  // 止まったときの括弧の濃さ ＝ 印の脈の谷（`sightMotion` の以後の alpha の下限 0.82）
  // 端へ動く間は、引き出した場所からの硬い線（山とのつながりを動きで見せる。v3）
  return {
    box: lerpBox(big, rest, u), k: 1, sightAlpha: lerp(1, 0.82, u), flash: 0, line: 1, badge: true, cardAlpha: 1, streak: 0,
    trail: 1 - seg(t, D - 40, D),
  };
}

export default defineEffect({
  situation: 'become_target_elsewhere',
  role: 'effect',
  label: '対象に取る（盤に無いカード）',
  color: '#ff5a3c',
  duration: () => D,
  // 帯が終わった瞬間から、同じ絵を盤に置く印が引き継ぐ（場の照準と同じ印・同じ key ＝ 同じ消え方）
  places: ({ payload, board }) => targetsOf(payload, board).map((c) => ({
    mark: 'targetSight',
    key: `${payload.chain}:${c.uid}`,
    at: { zone: c.zone },
    atMs: D,
    data: {
      uid: c.uid, chain: payload.chain, player: payload.player === 1 ? 1 : 0, zone: c.zone, elsewhere: true, code: c.code, faceUp: c.faceUp,
    },
  })),
  sounds: () => [{ at: 0, name: 'draw', gain: 0.4 }, { at: PULL + 170, name: 'set_card', gain: 0.45 }],
  layers: [
    {
      id: 'pull',
      label: '置き場から引き出して照準',
      space: 'screen',
      script: (ctx) => {
        const { THREE, kit } = ctx;
        const root = new THREE.Group();
        const { payload } = ctx;
        const canvas = canvasOf(ctx);
        const env = {
          zoneQuad: (k) => ctx.stage?.field?.zoneQuad?.(k) ?? null,
          hand: (p) => ctx.stage?.hud?.hand?.(p) ?? null,
          canvas,
        };
        const color = playerColor(ctx, payload?.player === 1 ? 1 : 0);
        const chain = payload?.chain ?? null;
        const items = targetsOf(payload, ctx.board).map((c) => {
          const spot = pileSpot(env, c.zone);
          if (!spot) return null;
          const big = bigBox(spot, canvas);
          const rest = restBox(spot, c.slot, canvas, c.count);
          const start = { cx: spot.cx, cy: spot.cy, w: rest.w * 0.9, h: rest.h * 0.9 };
          const label = labelOf(c.zone);
          const player = Number(c.zone.split(':')[1]) === 1 ? 1 : 0;
          const img = c.faceUp && c.code != null ? ctx.cards?.image?.(c.code) : ctx.cards?.sleeve?.(player);
          // 板の大きさ: 動く間の全部（山・引き出した大きいカードの括弧が一番広がったとき・残る小さいカード）
          const bigLay = layout(big, { k: 1.5, chain, label });
          const b1 = layoutBounds(bigLay);
          const b2 = layoutBounds(layout(rest, { chain, label }));
          const b3 = layoutBounds(layout(start, { chain, label }));
          const x0 = Math.floor(Math.max(0, Math.min(b1.x, b2.x, b3.x)));
          const y0 = Math.floor(Math.max(0, Math.min(b1.y, b2.y, b3.y)));
          const x1 = Math.ceil(Math.min(canvas.w, Math.max(b1.x + b1.w, b2.x + b2.w, b3.x + b3.w)));
          const y1 = Math.ceil(Math.min(canvas.h, Math.max(b1.y + b1.h, b2.y + b2.h, b3.y + b3.h)));
          const bw = Math.max(1, x1 - x0); const bh = Math.max(1, y1 - y0);
          const cv = typeof document !== 'undefined' ? document.createElement('canvas') : null;
          if (cv) { cv.width = bw; cv.height = bh; }
          const tex = cv ? kit.textureFromCanvas(cv) : null;
          const mesh = kit.quad(kit.plain(tex, 1), bw, bh);
          mesh.position.set(x0 + bw / 2, y0 + bh / 2, 0);
          add(root, mesh, 30 + c.slot);
          return {
            c, spot, big, rest, start, label, img, cv, tex, mesh, x0, y0,
          };
        }).filter(Boolean);

        return {
          root,
          update: ({ localMs: t }) => {
            items.forEach((it) => {
              const f = frameAt(t, it);
              it.mesh.visible = Boolean(f);
              const g2 = it.cv?.getContext?.('2d');
              if (!f || !g2) return;
              g2.setTransform(1, 0, 0, 1, 0, 0);
              g2.clearRect(0, 0, it.cv.width, it.cv.height);
              // 引き出す硬い線（山 → カード。3 本・白と金）。端へ動く間は引き出した場所 → カード
              const tail = f.trail > 0 ? { cx: it.big.cx, cy: it.big.cy } : { cx: it.spot.cx, cy: it.spot.cy };
              const sa = f.trail > 0 ? f.trail : f.streak;
              if (sa > 0) {
                const dx = f.box.cx - tail.cx; const dy = f.box.cy - tail.cy;
                const len = Math.hypot(dx, dy) || 1;
                const nx = -dy / len; const ny = dx / len;
                [[-0.32, '#ffffff', 3], [0, '#ffd21a', 4], [0.3, '#ffffff', 2.5]].forEach(([o, col, w]) => {
                  const off = o * f.box.w;
                  g2.globalAlpha = sa;
                  g2.strokeStyle = col;
                  g2.lineWidth = w;
                  g2.lineCap = 'butt';
                  g2.beginPath();
                  g2.moveTo(tail.cx + nx * off * 0.4 - it.x0, tail.cy + ny * off * 0.4 - it.y0);
                  g2.lineTo(f.box.cx + nx * off - dx * 0.25 - it.x0, f.box.cy + ny * off - dy * 0.25 - it.y0);
                  g2.stroke();
                });
                g2.globalAlpha = 1;
              }
              const lay = layout(f.box, { k: f.k, chain, label: it.label });
              paintElsewhere(g2, lay, it.x0, it.y0, {
                img: it.img, faceUp: it.c.faceUp, color, label: it.label, chain,
                sightAlpha: f.sightAlpha, flash: f.flash, line: f.line, badge: f.badge, cardAlpha: f.cardAlpha,
              });
              if (it.tex) it.tex.needsUpdate = true;
            });
          },
        };
      },
    },
  ],
}, 'become_target_elsewhere_effect.js');
